/**
 * XP-welcome-screen-style overlay played at moments that deserve a
 * flourish — opening a freshly-provisioned portal, or signing back in.
 *
 * Routes through the attention orchestrator (`lib/attention`) so any
 * tour spotlight, orientation redirect, or other attention-grabbing
 * moment serializes correctly. The component itself subscribes to the
 * orchestrator and renders the overlay whenever `current ===
 * ATTENTION_ID`. Callers don't dispatch events — they call one of two
 * helpers below:
 *
 *   - `playPortalWelcome()` — animation only, no navigation
 *   - `playWelcomeAndOpenPortal(navigate, slug)` — animation, then
 *     navigate to the portal at NAVIGATE_AT_MS so the desktop has time
 *     to mount behind the overlay before the fade-out begins
 *
 * Mounted once at the route root (`__root.tsx`) so it survives the
 * route transition from /onboard/new → /c/$slug/desktop.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { useNavigate } from '@tanstack/react-router';
import { requestAttention } from '../../lib/attention/attention';
import { useCurrentAttention } from '../../lib/attention/useAttention';
import { testIds } from '../../lib/testIds';

/**
 * Attention id for the orchestrator-driven portal-welcome moment
 * (the "Open my portal →" buttons). The sign-in welcome flourish
 * does NOT use the orchestrator — see SigninWelcomeBarrier below
 * for why.
 */
export const PORTAL_WELCOME_ATTENTION_ID = 'portal-welcome';

/**
 * Animation timing. Total overlay lifetime is FADE_IN + HOLD +
 * FADE_OUT = 3.0s (operator-locked 2026-05-27). For
 * playWelcomeAndOpenPortal the navigate fires at NAVIGATE_AT_MS so
 * the desktop has time to mount behind the overlay before the
 * fade-out begins.
 *
 * Reduced-motion path: short static hold, no fancy animation, but
 * the overlay still appears briefly so the click feels acknowledged.
 */
const FADE_IN_MS = 500;
const HOLD_MS = 1900;
const NAVIGATE_AT_MS = FADE_IN_MS + HOLD_MS; // 2.4s
const FADE_OUT_MS = 600;
const TOTAL_MS = NAVIGATE_AT_MS + FADE_OUT_MS; // 3.0s

const REDUCED_MOTION_TOTAL_MS = 500;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Pure-animation cycle. The orchestrator's `run` resolves when this
 * resolves; the component's visible flag is driven by
 * `useIsAttentionId(PORTAL_WELCOME_ATTENTION_ID)`, which flips true
 * for the duration of `run`. So this function just needs to wait
 * the right amount of time.
 *
 * Exposed (vs kept private) so consumers that ALREADY hold an
 * attention turn — like the sign-in flourish, which queues under
 * its own id `signin-welcome` — can compose the animation without
 * re-entering the queue (which would deadlock the orchestrator).
 */
export function playOverlayCycle(): Promise<void> {
  const reduced = prefersReducedMotion();
  const total = reduced ? REDUCED_MOTION_TOTAL_MS : TOTAL_MS;
  return new Promise<void>((resolve) => window.setTimeout(resolve, total));
}

/**
 * Submit a request to play the welcome overlay, no navigation.
 * Resolves when the orchestrator finishes the moment.
 *
 * Use case: any caller that wants to BOOK the welcome overlay slot
 * on the attention queue. The sign-in flourish does NOT use this —
 * it queues under id `signin-welcome` and calls `playOverlayCycle`
 * directly inside its own run, because composing one queued moment
 * inside another's run would deadlock.
 */
export function playPortalWelcome(): Promise<void> {
  return requestAttention({
    id: PORTAL_WELCOME_ATTENTION_ID,
    run: playOverlayCycle,
  });
}

/**
 * Play the welcome overlay, then navigate to the portal at
 * NAVIGATE_AT_MS so the desktop is mounted behind the overlay
 * before the fade-out begins. Returns a promise that resolves when
 * the orchestrator finishes the moment.
 *
 * Use case: the "Open my portal →" buttons on the onboarding
 * completed step and the "you already have a portal" intro branch.
 */
export function playWelcomeAndOpenPortal(
  navigate: ReturnType<typeof useNavigate>,
  slug: string,
): Promise<void> {
  return requestAttention({
    id: PORTAL_WELCOME_ATTENTION_ID,
    run: async () => {
      const reduced = prefersReducedMotion();
      const navigateAt = reduced
        ? Math.floor(REDUCED_MOTION_TOTAL_MS / 2)
        : NAVIGATE_AT_MS;
      const total = reduced ? REDUCED_MOTION_TOTAL_MS : TOTAL_MS;
      // Schedule navigate during the hold so the desktop mounts
      // under the overlay; orchestrator's `run` promise resolves
      // when the full lifetime elapses so the next queued moment
      // doesn't start until the fade-out is done.
      const navHandle = window.setTimeout(() => {
        void navigate({
          to: '/c/$clientSlug/desktop',
          params: { clientSlug: slug },
        });
      }, navigateAt);
      try {
        await new Promise<void>((resolve) =>
          window.setTimeout(resolve, total),
        );
      } finally {
        window.clearTimeout(navHandle);
      }
    },
  });
}

/**
 * Pure-visual overlay markup. Renders the XP welcome screen when
 * `visible` is true; AnimatePresence handles the fade-in/out. Used
 * by both the orchestrator-driven OnboardingCompletedOverlay (for
 * portal-welcome moments) and the synchronous SigninWelcomeBarrier
 * (for first-paint blocking on sign-in).
 *
 * Factored out so both surfaces share one source of truth for the
 * gradient, typography, animation timing, and testIds.
 */
function WelcomeOverlayMarkup({
  visible,
  /**
   * When true, paint at full opacity on the first frame (no fade-in).
   * Used by SigninWelcomeBarrier — its whole job is to cover the
   * desktop on first paint, and a 500ms fade-in defeats that. The
   * orchestrator-driven OnboardingCompletedOverlay uses the default
   * (false) because mid-session it should fade in smoothly over
   * whatever else is on screen.
   *
   * The fade-OUT exit animation still runs regardless — both surfaces
   * benefit from the soft dissolve to whatever's behind.
   */
  instantIn = false,
  /**
   * Optional override for the inner-text fade-in. Defaults to the
   * regular fade-in. SigninWelcomeBarrier sets this to skip the inner
   * scale/opacity animation so the text is solid on first paint too.
   */
  instantText = false,
}: {
  visible: boolean;
  instantIn?: boolean;
  instantText?: boolean;
}): ReactElement {
  return (
    <AnimatePresence>
      {visible ? (
        <motion.div
          key="portal-welcome-overlay"
          data-testid={testIds.onboard.welcomeOverlay}
          initial={{ opacity: instantIn ? 1 : 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: prefersReducedMotion() ? 0.1 : FADE_OUT_MS / 1000,
            ease: 'easeInOut',
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            // Nudge up ~10% from true center so the text sits in the
            // upper-mid band the XP welcome reference shows (true
            // center is too low against the bottom dark band's
            // visual weight).
            alignItems: 'center',
            paddingBottom: '20vh',
            // Right-anchor to mirror the XP welcome reference, where
            // "welcome" sits around 55% horizontal. "to whom it may
            // concern" is longer, so its right edge lands ~25-30%
            // from the right viewport edge.
            justifyContent: 'flex-end',
            paddingRight: 'clamp(80px, 18vw, 280px)',
            // XP boot-screen 3-band stacked gradient with thicker
            // bottom band per operator direction. Sharp px stops so
            // bands read as distinct strips, not a smooth fade.
            background: `
              radial-gradient(
                ellipse at 18% 25%,
                rgba(255, 255, 255, 0.35) 0%,
                rgba(255, 255, 255, 0.10) 25%,
                rgba(255, 255, 255, 0) 50%
              ),
              linear-gradient(
                180deg,
                #0a0a0a 0px,
                #0a0a0a 36px,
                #1a1a2e 64px,
                #222233 50%,
                #1a1a2e calc(100% - 100px),
                #0a0a0a calc(100% - 80px),
                #050510 100%
              )
            `,
            overflow: 'hidden',
          }}
        >
          <motion.div
            data-testid={testIds.onboard.welcomeOverlayText}
            initial={{
              opacity: instantText ? 1 : 0,
              scale: instantText ? 1 : 0.92,
            }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: prefersReducedMotion() ? 0.1 : FADE_IN_MS / 1000,
              ease: 'easeOut',
            }}
            style={{
              fontFamily: 'var(--xp-font-display)',
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: 'clamp(20px, 3.15vw, 40px)',
              color: '#ffffff',
              letterSpacing: '0.01em',
              textShadow:
                '0 0 18px rgba(255, 255, 255, 0.55), 0 1px 3px rgba(0, 0, 0, 0.25)',
              userSelect: 'none',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              textAlign: 'right',
            }}
          >
            to whom it may concern
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export function OnboardingCompletedOverlay(): ReactElement | null {
  const current = useCurrentAttention();
  const visible = current === PORTAL_WELCOME_ATTENTION_ID;
  // Local mount latch so the AnimatePresence exit animation gets to
  // play out after the orchestrator notifies `null`. Without this,
  // the component unmounts the instant the orchestrator transitions
  // and AnimatePresence has nothing to exit.
  const [renderLatch, setRenderLatch] = useState(false);
  useEffect(() => {
    if (visible) {
      setRenderLatch(true);
      return;
    }
    // visible just went false — let AnimatePresence's exit animation
    // finish, then drop the latch. The exit duration is FADE_OUT_MS;
    // add a small slack so we don't unmount mid-fade.
    const handle = window.setTimeout(
      () => setRenderLatch(false),
      FADE_OUT_MS + 100,
    );
    return () => window.clearTimeout(handle);
  }, [visible]);

  return <WelcomeOverlayMarkup visible={renderLatch && visible} />;
}

/**
 * Synchronous sign-in welcome barrier. Renders the XP welcome overlay
 * IMMEDIATELY on first paint after sign-in, BEFORE the desktop ever
 * mounts. This avoids the "desktop flash → overlay → desktop"
 * sequence that the orchestrator-driven path produced because
 * `requestAttention` was called from `useEffect`, which runs after
 * React commits the first paint.
 *
 * Decision rationale:
 *   - Orchestrator = serializes mid-session moments (one at a time).
 *     Right tool for portal-welcome, tour, orientation redirect.
 *   - Barrier = blocks first-paint synchronously. Right tool for
 *     "the user just landed; show this BEFORE anything else paints."
 *     Different problem; different mechanism.
 *
 * The barrier reads sessionStorage SYNCHRONOUSLY during its useState
 * initializer, so the very first render decides whether to show. No
 * effect-triggered flash possible.
 *
 * Usage: render this AS A SIBLING of Win95Desktop in __root.tsx.
 * Returns null when it shouldn't show; renders the full overlay when
 * it should. After SIGNIN_BARRIER_DURATION_MS, unmounts itself +
 * marks sessionStorage so it doesn't replay on remount.
 */
const SIGNIN_BARRIER_DURATION_MS = TOTAL_MS;
const SIGNIN_WELCOME_PLAYED_KEY = 'repancy:signin-welcome-played';

function readSigninBarrierShouldShow(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return (
      window.sessionStorage.getItem(SIGNIN_WELCOME_PLAYED_KEY) !== '1'
    );
  } catch {
    return false;
  }
}

function markSigninBarrierPlayed(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(SIGNIN_WELCOME_PLAYED_KEY, '1');
  } catch {
    /* incognito / quota; non-fatal */
  }
}

export function SigninWelcomeBarrier(): ReactElement | null {
  // Strict one-shot guard. The useState initializer runs ONCE on
  // mount; we both READ the sessionStorage gate AND WRITE it during
  // the same call. That way:
  //   - First mount in this browser session: gate empty, we render
  //     visible=true AND immediately mark sessionStorage. Any other
  //     concurrent / subsequent React render path that mounts this
  //     component reads the now-set flag and stays null forever.
  //   - Any later mount (HMR, parent re-render that conditionally
  //     remounts this subtree, tour Next click triggering a render
  //     cascade, etc.) reads the SET flag → starts with visible=false
  //     → returns null on every render → never re-displays.
  //
  // Previously we wrote the gate inside a useEffect, which ran AFTER
  // the first paint. If the component unmounted before that effect
  // fired (or before a stale-prev useEffect cleanup), the gate
  // stayed unset → next mount re-fired the overlay. Per operator
  // report 2026-05-27.
  const [visible, setVisible] = useState(() => {
    const shouldShow = readSigninBarrierShouldShow();
    if (shouldShow) {
      // Mark IMMEDIATELY so no future mount of this component shows
      // again, even if this mount's later effects never run (e.g.
      // unmount before the 3s timer, StrictMode double-mount, HMR).
      markSigninBarrierPlayed();
    }
    return shouldShow;
  });

  useEffect(() => {
    if (!visible) return;
    // sessionStorage already marked in useState initializer above —
    // this effect only owns the dismiss timer.
    const handle = window.setTimeout(() => {
      setVisible(false);
    }, SIGNIN_BARRIER_DURATION_MS);
    return () => window.clearTimeout(handle);
  }, [visible]);

  // Local mount latch matching OnboardingCompletedOverlay so the
  // exit animation gets to play.
  const [renderLatch, setRenderLatch] = useState(visible);
  useEffect(() => {
    if (visible) {
      setRenderLatch(true);
      return;
    }
    const handle = window.setTimeout(
      () => setRenderLatch(false),
      FADE_OUT_MS + 100,
    );
    return () => window.clearTimeout(handle);
  }, [visible]);

  if (!renderLatch) return null;
  // instantIn/instantText: the whole point of the barrier is to block
  // the desktop's first paint. A 500ms fade-in defeats that — the
  // overlay would be opacity 0 on frame 1, showing the desktop
  // underneath. Render fully opaque immediately; only the FADE-OUT
  // animation runs at end-of-life.
  return (
    <WelcomeOverlayMarkup
      visible={visible}
      instantIn
      instantText
    />
  );
}

/**
 * Test-only: reset the sessionStorage gate so a test can re-render
 * the barrier as if the user just signed in. NOT exported through the
 * module's barrel — import directly from the source file in tests.
 */
export function __resetSigninBarrierForTests(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(SIGNIN_WELCOME_PLAYED_KEY);
  } catch {
    /* ignore */
  }
}
